import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { exportMyData } from "@/lib/compliance.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/account/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy & Data — The Hospitality Reserve" },
      { name: "description", content: "Export your personal data or request account deletion under GDPR." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const doExport = useServerFn(exportMyData);
  const [downloading, setDownloading] = useState(false);

  const downloadExport = async () => {
    setDownloading(true);
    try {
      const { json } = await doExport();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hospitality-reserve-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-4 py-10 space-y-8">
        <header>
          <p className="tracking-wide-luxury text-accent text-xs">Privacy &amp; Data</p>
          <h1 className="mt-2 font-display text-3xl">Your data, your rights</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Under UK GDPR you can access a copy of your personal data or request its erasure at any time.
          </p>
        </header>

        <section className="rounded-lg border p-5 space-y-3">
          <h2 className="font-medium">Download my data (Article 15)</h2>
          <p className="text-sm text-muted-foreground">
            Get a machine-readable JSON bundle containing your profile, bookings, payouts, messages, reviews,
            notification preferences, RTW audit trail, cookie consent history, and any deletion or strike records.
            Every export is logged for audit purposes.
          </p>
          <Button onClick={downloadExport} disabled={downloading}>
            {downloading ? "Preparing…" : "Download my data (.json)"}
          </Button>
        </section>

        <section className="rounded-lg border p-5 space-y-3">
          <h2 className="font-medium">Delete my account (Article 17)</h2>
          <p className="text-sm text-muted-foreground">
            You can request permanent deletion of your account. We keep a 7-day cooling-off window in case
            you change your mind, and we retain financial records (bookings, payouts, invoices) for 6 years
            as required by HMRC.
          </p>
          <Button asChild variant="destructive">
            <Link to="/account/delete">Start deletion request</Link>
          </Button>
        </section>

        <section className="rounded-lg border p-5 space-y-3">
          <h2 className="font-medium">Cookie preferences</h2>
          <p className="text-sm text-muted-foreground">
            Update the cookie categories you accept. Necessary cookies are always on.
          </p>
          <Button
            variant="outline"
            onClick={() => window.dispatchEvent(new CustomEvent("reserve:open-cookie-preferences"))}
          >
            Manage cookie preferences
          </Button>
        </section>

        <section className="text-xs text-muted-foreground">
          Questions? Contact <a className="underline" href="mailto:privacy@thehospitalityreserve.com">privacy@thehospitalityreserve.com</a>.
        </section>
      </main>
    </div>
  );
}
